/** @typedef {"operator" | "clinician"} AssistanceRole */

/** @typedef {"chat" | "presence" | "device" | "command"} AssistanceType */

/**
 * @typedef {Object} AssistanceFrom
 * @property {AssistanceRole} role
 * @property {number} [id]
 * @property {string} [name]
 */

/**
 * @typedef {Object} AssistanceMessage
 * @property {number} schema
 * @property {AssistanceType} type
 * @property {number} appointmentId
 * @property {AssistanceFrom} from
 * @property {number} at
 * @property {Record<string, unknown>} payload
 */

export const ASSISTANCE_SCHEMA = 1;

/**
 * @param {unknown} raw
 * @returns {AssistanceMessage | null}
 */
export function parseAssistanceMessage (raw) {
	if (raw == null || raw === '') return null;
	let obj = raw;
	if (typeof raw === 'string') {
		try {
			obj = JSON.parse(raw);
		} catch {
			return null;
		}
	}
	if (!obj || typeof obj !== 'object') return null;
	const schema = Number(obj.schema);
	const type = obj.type;
	const appointmentId = Number(obj.appointmentId);
	const from = obj.from;
	const at = Number(obj.at);
	const payload = obj.payload;
	if (
		schema !== ASSISTANCE_SCHEMA ||
		!type ||
		!Number.isFinite(appointmentId) ||
		!from ||
		typeof from !== 'object' ||
		!Number.isFinite(at) ||
		!payload ||
		typeof payload !== 'object'
	) {
		return null;
	}
	const role = from.role;
	if (role !== 'operator' && role !== 'clinician') return null;
	if (type !== 'chat' && type !== 'presence' && type !== 'device' && type !== 'command') return null;
	return /** @type {AssistanceMessage} */ ({
		schema: ASSISTANCE_SCHEMA,
		type,
		appointmentId,
		from: {
			role,
			id: from.id != null ? Number(from.id) : undefined,
			name: typeof from.name === 'string' ? from.name : undefined,
		},
		at,
		payload,
	});
}

/**
 * @param {Object} params
 * @param {AssistanceType} params.type
 * @param {number} params.appointmentId
 * @param {AssistanceFrom} params.from
 * @param {Record<string, unknown>} params.payload
 * @param {number} [params.at]
 * @returns {AssistanceMessage}
 */
export function buildAssistanceMessage ({ type, appointmentId, from, payload, at = Date.now() }) {
	return { schema: ASSISTANCE_SCHEMA, type, appointmentId, from, at, payload };
}

/**
 * @param {AssistanceMessage} msg
 * @returns {string}
 */
export function serializeAssistanceMessage (msg) {
	return JSON.stringify(msg);
}

/**
 * Human-readable one-liner for the system log.
 * @param {AssistanceMessage} msg
 */
export function formatAssistanceLine (msg) {
	const who = msg.from.name || msg.from.role;
	switch (msg.type) {
		case 'chat':
			return `${who}: ${msg.payload.text ?? ''}`;
		case 'presence':
			return `${who} ${msg.payload.state ?? 'updated'}`;
		case 'device': {
			const ev = msg.payload.event ?? 'event';
			if (ev === 'heartRate') return `${who} · HR ${msg.payload.heartRateBpm ?? '—'} BPM`;
			return `${who} · ${ev}`;
		}
		case 'command':
			return `${who} → ${msg.payload.action ?? 'command'}`;
		default:
			return `${who}: ${msg.type}`;
	}
}
